import { yup } from '@framework/validation/initYup.ts';
import { FormValidatorBuilder } from '../../../../../validator/form-builder/FormValidatorBuilder.ts';
import { getWidgetName } from '../../../../../validator/form-builder/getWidgetName.ts';
import { getParentValidationScope } from '../../../../../validator/utils/getParentValidationScope.ts';
import { remapReferencesUpwardInScope } from '../../../../../validator/utils/remapReferencesUpwardInScope.ts';
import {
  type IParentWidgetScope,
  type IWidgetType,
} from '../../../IWidgetType.ts';
import { type IRepeatableWidget } from './IRepeatableWidget.ts';

export function createRepeatableChildrenSchema(
  widget: IRepeatableWidget,
  section: number,
  page: number,
  parentScope: IParentWidgetScope,
) {
  const { children } = widget;
  const childrenSchema = FormValidatorBuilder.createWidgetsSchemaShape(
    children,
    section,
    page,
    parentScope,
  );
  delete childrenSchema.__isWidgetSchemaShape;
  return yup.object().shape(childrenSchema);
}

/**
 * Funzione che crea lo schema di validazione per un widget di tipo Repeatable.
 * Il risultato è un oggetto { [fielName]: arraySchema } costruito ricorsivamente su
 * tutti i campi all'interno.
 */
export const createRepeatableSchema: IWidgetType<IRepeatableWidget>['createFormSchema'] =
  ({ widget, section, page, parentScope }) => {
    // Recupero lo scope di validazione del widget lista ripetibile da passare ai children
    const widgetScope = getParentValidationScope(widget, parentScope);
    const fieldName = getWidgetName(widget);
    const childrenSchema = createRepeatableChildrenSchema(
      widget,
      section,
      page,
      // Le referenze all'interno dei figli devono essere riferite al widget
      // padre, quindi le rimappiamo
      remapReferencesUpwardInScope(widget, widgetScope),
    );

    // Numero di righe iniziali da materializzare nel default. Concentrato qui
    // perché il default deve riflettere min/showAtLeastOne del widget così che
    // Yup ricorra automaticamente sui default annidati (LR-in-LR atomico).
    const minRows = widget.rules?.repeatableRange?.min ?? 0;
    const showAtLeastOne = widget.layout?.showAtLeastOne ? 1 : 0;
    const initialRows = minRows > 0 ? minRows : showAtLeastOne;

    const arraySchema = yup
      .array()
      .of(childrenSchema)
      // Recupera dallo schema dei figli le dipendenze dei when annidati
      // per applicarle alla lista così da poter far sì che yup ordini
      // correttamente i cast (e non arrivino undefined con campi in realtà valorizzati)
      .addChildrenParentDeps()
      .required()
      .default(() =>
        initialRows > 0
          ? Array.from({ length: initialRows }, () =>
              childrenSchema.cast({}, { assert: false }),
            )
          : [],
      );

    return {
      [fieldName]: arraySchema,
    };
  };
